import type { Tutorial } from '../../shared/types.js';

export const securityReviewFlowTutorial: Tutorial = {
  name: 'security-review-flow',
  title: 'Schedule a recurring security review',
  description:
    'Build an automation that runs on a cron schedule, uses an agent task to security-review a GitHub repo of your choice, and opens a pull request with the findings. Best done after the GitHub App is installed.',
  category: 'flows',
  docPages: ['concepts/flows'],
  agenda: [
    'Start a scheduled review automation',
    'Describe it and pick a model',
    'Watch Reflex build the graph',
    'Review the schedule and publish',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'Automate a recurring security review',
      body: "Now that the GitHub App is installed, let's schedule an agent to review one of your repos on a recurring basis and open a PR with the agent's findings. You'll describe the automation in plain English and let Reflex build the graph for you.",
      path: '/automations',
    },
    {
      id: 'new-flow',
      kind: 'action',
      trigger: 'click',
      title: 'Start a new automation',
      body: 'Click "New Automation" to open the automation builder.',
      target: '[data-tutorial-id="flows-new"]',
      placement: 'bottom',
    },
    {
      id: 'describe',
      kind: 'action',
      trigger: 'input',
      title: 'Describe the automation',
      body: 'In the **Generate flow with AI** box, tell Reflex what to build in plain English: the schedule, the repo, and what the agent should do. Use **Fill example** to start from a sample, then swap in any repo the GitHub App can access.',
      example:
        'Every weekday at 9am, run an agent task that performs a security review of the owner/repo repository and opens a pull request summarizing the vulnerabilities it finds.',
      target: '[data-tutorial-id="flow-generate-prompt"]',
      placement: 'bottom',
      path: '/automations/new',
    },
    {
      id: 'model-key',
      kind: 'info',
      title: 'Pick a model and key',
      body: 'Choose the model and provider key that the automation should use. If your organization has no key yet, you can add one right here without leaving the page.',
      target: '[data-tutorial-id="flow-generate-model"]',
      placement: 'bottom',
    },
    {
      id: 'generate',
      kind: 'action',
      trigger: 'click',
      title: 'Generate the automation',
      body: 'Click the **Generate automation** arrow button. Reflex opens the editor and builds the graph live: a cron trigger on your schedule, an agent task that runs the security review, and a step that opens the pull request.',
      target: '[data-tutorial-id="flow-generate-submit"]',
      placement: 'bottom',
    },
    {
      id: 'watch-build',
      kind: 'info',
      title: 'Watch Reflex build it',
      body: "Reflex reads your description and assembles the automation right here in the chat. Watch it stream each step as it gets added. You'll see it choose the trigger, write the agent task, and wire up the pull-request (PR) step as it progresses.",
      target: '[data-testid="flow-editor-chat"]',
      placement: 'left',
    },
    {
      id: 'watch-graph',
      kind: 'info',
      title: 'The graph fills in live',
      body: 'New nodes appear on the canvas as Reflex adds them: a cron trigger on your schedule, an agent task that runs the security review, and a step that opens the pull request. The graph is the same one that will run on the schedule.',
      target: '[data-tutorial-id="flow-editor-canvas"]',
      placement: 'right',
    },
    {
      id: 'review-publish',
      kind: 'info',
      // No dim/lock: the build is done and the page is now the user's to poke
      // at - selecting the trigger, opening the agent task, editing the repo -
      // before they commit. The tooltip just hangs in the corner as a reminder.
      dim: false,
      title: 'Review the schedule, then publish',
      body: 'Take your time here; this is your page to configure. Select the trigger on the canvas to confirm the cron schedule and timezone, then open the agent task to check the target repo and prompt. Change anything you like. When it looks right, click "Publish" to make the automation live; it will run automatically on the schedule from now on.',
    },
  ],
  // The one prerequisite that genuinely relates to building this flow: the
  // GitHub App (installed via the connect-github tour) is what grants repo
  // access and lets the agent open the pull request.
  related: [{ tutorialName: 'connect-github', label: 'Install the GitHub App for repo access' }],
};
